import { Link, Typography } from "@mui/material";
import {
  ErrorPanel,
  ASTableV2,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2Cell,
  ASTableV2Body,
  ASTableV2RowSkeleton,
  ASTableV2RowEmpty,
  globalDateTimeFormat,
  RelativeTime
} from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import dayjs from "dayjs";
import { t } from "i18next";

import { useAppSelector } from "~/app/store";
import { mixpanelTrack, useMixpanelContextName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  useGetUnifyPortErrorsQuery,
  useGetUnifyRobotErrorsQuery
} from "~/redux/warehouse/unify.openApi";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import {
  buildPortIdToWorkstationMap,
  getStatusLookupUrl,
  getWorkstationLabel,
  toUnifyErrorRow,
  UnifyErrorEvent
} from "./unify.utils";

const COLUMN_COUNT = 9;
// Cap the error-description column so long messages wrap instead of widening
// the whole table; other columns size to their content.
const GRID_TEMPLATE_COLUMNS =
  "auto auto auto auto auto auto minmax(0, 240px) auto auto";

export const UnifyErrors = () => {
  const trackedPageName = useMixpanelContextName("Cube Analytics");
  const selectedGridId = useAppSelector(
    (state) => state.serviceSupport.selectedGridId
  );
  const selectedWorkstationId = useAppSelector(
    (state) => state.serviceSupport.selectedWorkstationId
  );

  const { data: workstations } = useGetWorkstationsQuery();
  const portIdToWorkstation = buildPortIdToWorkstationMap(
    workstations,
    selectedGridId
  );

  const queryArg = selectedGridId
    ? {
        autostoreGridId: selectedGridId,
        workstationId: selectedWorkstationId,
        limit: null
      }
    : skipToken;

  // Use currentData (not data) so the table clears when the arg changes to
  // skipToken (grid set to None) instead of retaining the previous grid's rows.
  const {
    currentData: portErrors,
    isFetching: isPortErrorsFetching,
    error: portErrorsFetchError,
    refetch: refetchPortErrors
  } = useGetUnifyPortErrorsQuery(queryArg);

  const {
    currentData: robotErrors,
    isFetching: isRobotErrorsFetching,
    error: robotErrorsFetchError,
    refetch: refetchRobotErrors
  } = useGetUnifyRobotErrorsQuery(queryArg);

  const isFetching = isPortErrorsFetching || isRobotErrorsFetching;
  const fetchError = portErrorsFetchError ?? robotErrorsFetchError;

  const combined: UnifyErrorEvent[] = [
    ...(portErrors ?? []),
    ...(robotErrors ?? [])
  ];
  const rows = combined
    .toSorted((a, b) =>
      dayjs(a.item.timestamp).isBefore(dayjs(b.item.timestamp)) ? 1 : -1
    )
    .map(toUnifyErrorRow);

  return (
    <>
      {fetchError && (
        <ErrorPanel
          message={getMessageFromRtkError(fetchError)}
          tryAgain={{
            onTryAgain: async () => {
              await Promise.all([refetchPortErrors(), refetchRobotErrors()]);
            }
          }}
        />
      )}
      {!fetchError && (
        <ASTableV2
          gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
          isLoading={false}
        >
          <ASTableV2Header>
            <ASTableV2Row>
              <ASTableV2Cell>
                <Typography variant="tableBody">
                  {t("date and time")}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("workstation")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("port id")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("bin id")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("robot id")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("error code")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">
                  {t("error description")}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("error type")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("status")}</Typography>
              </ASTableV2Cell>
            </ASTableV2Row>
          </ASTableV2Header>
          <ASTableV2Body>
            {isFetching && (
              <ASTableV2RowSkeleton columns={COLUMN_COUNT} rows={1} />
            )}
            {!isFetching && !rows.length && (
              <ASTableV2RowEmpty>{t("no results")}</ASTableV2RowEmpty>
            )}
            {!isFetching &&
              rows.map((row, i) => {
                const statusLookupUrl = getStatusLookupUrl(
                  row.errorCode,
                  row.errorDescription
                );
                return (
                  <ASTableV2Row key={i}>
                    <ASTableV2Cell
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start"
                      }}
                    >
                      <RelativeTime time={row.timestamp} variant="tableBody" />
                      <Typography>
                        {dayjs(row.timestamp).format(globalDateTimeFormat)}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">
                        {getWorkstationLabel(portIdToWorkstation, row.portId)}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">{row.portId}</Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">{row.binId}</Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">{row.robotId}</Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">
                        {row.errorCode}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell sx={{ wordBreak: "break-word" }}>
                      {statusLookupUrl ? (
                        <Link
                          href={statusLookupUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="tableBody"
                          onClick={() =>
                            mixpanelTrack({
                              trackedPageName,
                              type: "Button Click",
                              label: "Navigate to Error Status Lookup",
                              eventProperties: {
                                "Error Description": row.errorDescription,
                                "Error Code": row.errorCode,
                                "Error Type": row.source
                              }
                            })
                          }
                        >
                          {row.errorDescription}
                        </Link>
                      ) : (
                        <Typography variant="tableBody">
                          {row.errorDescription}
                        </Typography>
                      )}
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">
                        {t(row.source.toLowerCase() as "port" | "robot")}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">{row.status}</Typography>
                    </ASTableV2Cell>
                  </ASTableV2Row>
                );
              })}
          </ASTableV2Body>
        </ASTableV2>
      )}
    </>
  );
};
